import React, { useState, useMemo } from 'react';
import { Table, Input, Select, Tag, Card, Row, Col, Space, Button, Tooltip, Statistic, Alert } from 'antd';
import { SearchOutlined, ReloadOutlined, ClockCircleOutlined, CheckCircleOutlined, WarningOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useListServerProcessesQuery } from '../store/api';
import dayjs from 'dayjs';

const typeColors = {
  cron: 'blue',
  service: 'green',
  inotify: 'orange',
  reboot_script: 'purple',
  docker: 'cyan',
};

const statusColors = {
  ok: 'success',
  running: 'processing',
  error: 'error',
  missing: 'error',
  stopped: 'error',
  timeout: 'error',
  stale: 'warning',
  error_in_log: 'error',
  no_ssh: 'warning',
  unknown: 'default',
  warning: 'warning',
};

const statusLabels = {
  ok: 'OK',
  error: 'ERROR',
  missing: 'NO EXISTE',
  stopped: 'DETENIDO',
  timeout: 'TIMEOUT',
  stale: 'SIN ACTIVIDAD',
  error_in_log: 'ERROR EN LOG',
  no_ssh: 'SIN SSH',
  unknown: 'DESCONOCIDO',
  warning: 'ALERTA',
};

const ServerProcesses = () => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ is_active: '1' });
  const [search, setSearch] = useState('');

  const queryParams = useMemo(() => ({
    page,
    limit: 20,
    ...filters,
    ...(search ? { search } : {}),
  }), [page, filters, search]);

  const { data, isLoading, isFetching, refetch } = useListServerProcessesQuery(queryParams, {
    pollingInterval: 120000,
  });

  const rows = data?.data || [];
  const total = data?.total || 0;
  const filterOptions = data?.filterOptions || {};
  const summary = data?.summary || {};
  const failing = Number(summary.failing || 0);
  const unknown = Number(summary.unknown || 0);
  const noSsh = Number(summary.no_ssh || 0);

  const handleFilterChange = (key, value) => {
    setPage(1);
    setFilters(prev => ({ ...prev, [key]: value || '' }));
  };

  const columns = [
    {
      title: 'Servidor',
      dataIndex: 'server_name',
      width: 170,
      render: (name, r) => (
        <Tooltip title={`${r.server_ip}${r.estado_servidor_nombre ? ` · ${r.estado_servidor_nombre}` : ''}`}>
          <span style={{ fontWeight: 500 }}>{name || r.estado_servidor_nombre || r.server_ip}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'process_type',
      width: 100,
      render: (t) => <Tag color={typeColors[t] || 'default'}>{t}</Tag>,
    },
    {
      title: 'Proyecto',
      dataIndex: 'project',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Horario',
      dataIndex: 'schedule',
      width: 120,
      render: (s) => s ? <code style={{ fontSize: 12 }}>{s}</code> : '—',
    },
    {
      title: 'Descripción',
      dataIndex: 'description_human',
      ellipsis: true,
    },
    {
      title: 'Script',
      dataIndex: 'script_path',
      width: 200,
      ellipsis: true,
      render: (p) => p ? <code style={{ fontSize: 11 }}>{p}</code> : '—',
    },
    {
      title: 'Activo',
      dataIndex: 'is_active',
      width: 70,
      align: 'center',
      render: (v) => v ? <Tag color="green">Sí</Tag> : <Tag color="red">No</Tag>,
    },
    {
      title: 'Estado',
      dataIndex: 'last_status',
      width: 110,
      render: (s) => <Tag color={statusColors[s] || 'default'}>{statusLabels[s] || s || '—'}</Tag>,
    },
    {
      title: 'Base de datos afectada',
      dataIndex: 'connects_to_db',
      width: 180,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Escribe en',
      dataIndex: 'writes_to',
      width: 180,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Origen',
      dataIndex: 'cron_source',
      width: 80,
      render: (v, r) => v ? <Tag>{v}{r.cron_user && r.cron_user !== 'root' ? `:${r.cron_user}` : ''}</Tag> : '—',
    },
    {
      title: 'Último check',
      dataIndex: 'last_run',
      width: 130,
      render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—',
    },
    {
      title: 'Último log',
      dataIndex: 'last_log_seen',
      width: 130,
      render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—',
    },
    {
      title: 'Avg (s)',
      dataIndex: 'execution_time_avg_sec',
      width: 70,
      align: 'right',
      render: (v) => v != null ? v : '—',
    },
  ];

  return (
    <div style={{ padding: '0 8px' }}>
      <Card
        title={
          <Space>
            <ClockCircleOutlined />
            <span>Procesos de Servidores</span>
            <Tag>{total} procesos</Tag>
          </Space>
        }
        extra={
          <Button icon={<ReloadOutlined spin={isFetching} />} onClick={refetch}>
            Actualizar
          </Button>
        }
      >
        {(failing > 0 || noSsh > 0) && (
          <Alert
            type={failing > 0 ? 'error' : 'warning'}
            showIcon
            style={{ marginBottom: 16 }}
            message={`${failing} proceso(s) con falla y ${noSsh} sin verificación SSH`}
            description="Los registros con error, detenidos o faltantes aparecen primero en la tabla."
          />
        )}

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Total" value={Number(summary.total || total || 0)} prefix={<ClockCircleOutlined />} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="OK" value={Number(summary.ok || 0)} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Fallas" value={failing} valueStyle={{ color: failing ? '#f5222d' : undefined }} prefix={<WarningOutlined />} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Sin verificar" value={unknown + noSsh} prefix={<QuestionCircleOutlined />} />
            </Card>
          </Col>
        </Row>

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Buscar..."
              prefix={<SearchOutlined />}
              allowClear
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Servidor"
              allowClear
              style={{ width: '100%' }}
              onChange={(v) => handleFilterChange('server_ip', v)}
              options={(filterOptions.server_ip || []).map(ip => ({ label: ip, value: ip }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Proyecto"
              allowClear
              style={{ width: '100%' }}
              onChange={(v) => handleFilterChange('project', v)}
              options={(filterOptions.project || []).map(p => ({ label: p, value: p }))}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder="Tipo"
              allowClear
              style={{ width: '100%' }}
              onChange={(v) => handleFilterChange('process_type', v)}
              options={(filterOptions.process_type || []).map(t => ({ label: t, value: t }))}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder="Estado"
              allowClear
              style={{ width: '100%' }}
              onChange={(v) => handleFilterChange('last_status', v)}
              options={(filterOptions.last_status || []).map(s => ({ label: s, value: s }))}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder="Activo"
              allowClear
              style={{ width: '100%' }}
              onChange={(v) => handleFilterChange('is_active', v)}
              options={[{ label: 'Sí', value: '1' }, { label: 'No', value: '0' }]}
            />
          </Col>
        </Row>

        <Table
          dataSource={rows}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          scroll={{ x: 1200 }}
          rowClassName={(record) => (
            ['error', 'missing', 'stopped', 'timeout', 'error_in_log', 'stale'].includes(record.last_status) ? 'row-error' : ''
          )}
          pagination={{
            current: page,
            pageSize: 20,
            total,
            showSizeChanger: false,
            showTotal: (t, range) => `${range[0]}-${range[1]} de ${t}`,
            onChange: (p) => setPage(p),
          }}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: '8px 0', fontSize: 13 }}>
                <Row gutter={[16, 8]}>
                  <Col span={12}><strong>Script:</strong> <code>{record.script_path || '—'}</code></Col>
                  <Col span={12}><strong>Log:</strong> <code>{record.log_path || '—'}</code></Col>
                  <Col span={12}><strong>Conecta a DB:</strong> {record.connects_to_db || '—'}</Col>
                  <Col span={12}><strong>Conecta a servidor:</strong> {record.connects_to_server || '—'}</Col>
                  <Col span={12}><strong>Escribe en:</strong> {record.writes_to || '—'}</Col>
                  <Col span={12}><strong>Usa lock:</strong> {record.uses_lock ? 'Sí' : 'No'}</Col>
                  <Col span={12}><strong>Riesgo overlap:</strong> {record.overlap_risk || '—'}</Col>
                  <Col span={12}><strong>Tiempo avg:</strong> {record.execution_time_avg_sec != null ? `${record.execution_time_avg_sec}s` : '—'}</Col>
                  {record.last_error && (
                    <Col span={24}><strong>Último error:</strong> <code style={{ color: 'red' }}>{record.last_error}</code></Col>
                  )}
                  {record.notes && (
                    <Col span={24}><strong>Notas:</strong> {record.notes}</Col>
                  )}
                </Row>
              </div>
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default ServerProcesses;
